/** Type Set studio state. The reducer owns one product, one journey, and one
 * reversible type fitting. No image or palette state is smuggled in here. */

import { createContext, useContext, useEffect, useMemo, useReducer, type ReactNode } from "react";

import type {
  DirectionCard,
  EntryRoute,
  PreviewCopy,
  SharedBrief,
  TypeStudioAction as ExactTypeStudioAction,
  TypeStudioSession,
  TypeRecommendation,
} from "@core/index.js";
import { pendingQuestionIds } from "@core/index.js";

import { composeDirection } from "../wiring/engines";
import { questionQueue } from "../wiring/questions";
import { applyExactTypeAction, createExactTypeStudio, legacyTypeForSession, typeCatalogForSession } from "../wiring/typeLibrary";

export type StudioStep = "path" | "brief" | "words" | "processing" | "direction" | "lab" | "export";

export type TypeTuning = {
  displayWeight: number;
  bodyWeight: number;
  utilityWeight: number;
  scale: number;
  bodySize: number;
  lineHeight: number;
  tracking: number;
  measure: number;
  alignment: "left" | "center";
  axisValues: Record<"display" | "body" | "utility", Record<string, number>>;
};

export const DEFAULT_TYPE_TUNING: TypeTuning = {
  displayWeight: 600,
  bodyWeight: 400,
  utilityWeight: 500,
  scale: 1,
  bodySize: 1,
  lineHeight: 1.28,
  tracking: 0,
  measure: 62,
  alignment: "left",
  axisValues: { display: {}, body: {}, utility: {} },
};

const defaultTypeTuning = (): TypeTuning => ({
  ...DEFAULT_TYPE_TUNING,
  axisValues: { display: {}, body: {}, utility: {} },
});

/** Temporary recovery only: this tab may survive a refresh without turning
 * an unfinished direction into the remembered-project consent flow. */
export const ACTIVE_STUDIO_SESSION_KEY = "type-set.active-studio-session.v1";

export type StudioState = {
  entry: EntryRoute | null;
  step: StudioStep;
  brief: SharedBrief;
  rememberedCollectionId: string | null;
  typeStudio: TypeStudioSession | null;
  typeTuning: TypeTuning;
  typeResult: TypeRecommendation | null;
  selectedTypeId: string | null;
  projectId: string;
  projectName: string;
  consentRemember: boolean;
};

const newProjectId = () => `ts-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const freshState = (): StudioState => ({
  entry: null,
  step: "path",
  brief: { route: "type", writingSystems: ["latin"] },
  rememberedCollectionId: null,
  typeStudio: null,
  typeTuning: defaultTypeTuning(),
  typeResult: null,
  selectedTypeId: null,
  projectId: newProjectId(),
  projectName: "",
  consentRemember: false,
});

type JsonRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is JsonRecord => typeof value === "object" && value !== null && !Array.isArray(value);
const isStringList = (value: unknown): value is string[] => Array.isArray(value) && value.every((item) => typeof item === "string");
const isStoredRole = (value: unknown): value is string | null => value === null || typeof value === "string";

const storedTuning = (value: unknown): TypeTuning | null => {
  if (!isRecord(value) || !isRecord(value.axisValues)) return null;
  const numberKeys = ["displayWeight", "bodyWeight", "utilityWeight", "scale", "bodySize", "lineHeight", "tracking", "measure"] as const;
  if (numberKeys.some((key) => typeof value[key] !== "number" || !Number.isFinite(value[key]))) return null;
  if (value.alignment !== "left" && value.alignment !== "center") return null;
  const axisValues = { display: {}, body: {}, utility: {} } as TypeTuning["axisValues"];
  for (const role of ["display", "body", "utility"] as const) {
    const axes = value.axisValues[role];
    if (!isRecord(axes)) return null;
    for (const [tag, axisValue] of Object.entries(axes)) {
      if (typeof axisValue !== "number" || !Number.isFinite(axisValue)) return null;
      axisValues[role][tag] = axisValue;
    }
  }
  return {
    displayWeight: value.displayWeight as number,
    bodyWeight: value.bodyWeight as number,
    utilityWeight: value.utilityWeight as number,
    scale: value.scale as number,
    bodySize: value.bodySize as number,
    lineHeight: value.lineHeight as number,
    tracking: value.tracking as number,
    measure: value.measure as number,
    alignment: value.alignment,
    axisValues,
  };
};

const restoreStudioSession = (brief: SharedBrief, value: unknown): TypeStudioSession | null => {
  if (!isRecord(value) || !Number.isSafeInteger(value.seed) || (value.seed as number) < 0 || !isRecord(value.locked)) return null;
  const locked = value.locked;
  if (!isStoredRole(locked.display) || !isStoredRole(locked.body) || !isStoredRole(locked.utility)) return null;
  const roleLocks: Record<"display" | "body" | "utility", string | null> = {
    display: locked.display,
    body: locked.body,
    utility: locked.utility,
  };
  if (!isStringList(value.starredFontIds) || !isStringList(value.excludedFontIds)) return null;
  if (value.selectedDirectionId !== null && typeof value.selectedDirectionId !== "string") return null;

  try {
    let session = createExactTypeStudio(brief, value.seed as number);
    if (!session) return null;
    for (const fontId of value.excludedFontIds) {
      session = applyExactTypeAction(session, { type: "excludeFont", fontId });
    }
    for (const fontId of value.starredFontIds) {
      session = applyExactTypeAction(session, { type: "toggleStarFont", fontId });
    }
    for (const role of ["display", "body", "utility"] as const) {
      const fontId = roleLocks[role];
      if (fontId) session = applyExactTypeAction(session, { type: "setRoleFont", role, fontId });
    }
    if (
      typeof value.selectedDirectionId === "string"
      && session.recommendation.directions.some((direction) => direction.id === value.selectedDirectionId)
    ) {
      session = applyExactTypeAction(session, { type: "selectDirection", directionId: value.selectedDirectionId });
    }
    // A refresh is a recovery checkpoint. New edits remain undoable from here,
    // without trusting an unvalidated history chain from browser storage.
    return { ...session, past: [], future: [] };
  } catch {
    return null;
  }
};

const restoredState = (): StudioState | null => {
  if (typeof window === "undefined" || window.location.hash.replace(/^#/, "") !== "/studio") return null;
  try {
    const raw = window.sessionStorage.getItem(ACTIVE_STUDIO_SESSION_KEY);
    if (!raw) return null;
    const saved: unknown = JSON.parse(raw);
    if (!isRecord(saved) || saved.schemaVersion !== 1 || !isRecord(saved.brief)) return null;
    const brief = saved.brief as SharedBrief;
    if (brief.route !== "type") return null;
    const typeStudio = restoreStudioSession(brief, saved.current);
    const typeTuning = storedTuning(saved.typeTuning);
    if (!typeStudio || !typeTuning) return null;
    const step = saved.step === "lab" || saved.step === "export" ? saved.step : "direction";
    return {
      ...freshState(),
      entry: "type",
      step,
      brief,
      typeStudio,
      typeTuning,
      typeResult: legacyTypeForSession(typeStudio),
      selectedTypeId: typeStudio.current.selectedDirectionId,
      projectId: typeof saved.projectId === "string" && saved.projectId ? saved.projectId : newProjectId(),
      projectName: typeof saved.projectName === "string" ? saved.projectName : "",
    };
  } catch {
    return null;
  }
};

const initialState = (): StudioState => restoredState() ?? freshState();

const startingBrief = (): SharedBrief => ({ route: "type", writingSystems: ["latin"] });

export type StudioAction =
  | { type: "enter"; entry: EntryRoute; rememberedCollectionId?: string | null }
  | { type: "setStep"; step: StudioStep }
  | { type: "setBrief"; brief: SharedBrief }
  | { type: "setPreviewCopy"; previewCopy: PreviewCopy }
  | { type: "setProjectName"; name: string }
  | { type: "typeStudioReady"; session: TypeStudioSession }
  | { type: "typeStudioAction"; action: ExactTypeStudioAction }
  | { type: "setTypeTuning"; tuning: Partial<TypeTuning> }
  | { type: "resetTypeTuning" }
  | { type: "typeReady"; result: TypeRecommendation }
  | { type: "selectType"; systemId: string }
  | { type: "resetType" }
  | { type: "resetAll" }
  | { type: "setConsent"; consent: boolean }
  | { type: "projectLoaded"; brief: SharedBrief; typeResult: TypeRecommendation; selectedTypeId: string | null; projectId: string; projectName: string; entry: "type" };

const resetTypeFields = (state: StudioState): StudioState => ({
  ...state,
  typeStudio: null,
  typeTuning: defaultTypeTuning(),
  typeResult: null,
  selectedTypeId: null,
  brief: startingBrief(),
  step: "path",
});

const reducer = (state: StudioState, action: StudioAction): StudioState => {
  switch (action.type) {
    case "enter": {
      const fresh = freshState();
      return {
        ...fresh,
        entry: "type",
        rememberedCollectionId: action.rememberedCollectionId ?? null,
      };
    }
    case "setStep":
      return { ...state, step: action.step };
    case "setBrief":
      return { ...state, brief: { ...action.brief, route: "type" } };
    case "setPreviewCopy":
      return { ...state, brief: { ...state.brief, previewCopy: action.previewCopy } };
    case "setProjectName":
      return { ...state, projectName: action.name };
    case "typeStudioReady": {
      const typeResult = legacyTypeForSession(action.session);
      return {
        ...state,
        typeStudio: action.session,
        typeResult,
        selectedTypeId: action.session.current.selectedDirectionId,
      };
    }
    case "typeStudioAction": {
      if (!state.typeStudio) return state;
      const typeStudio = applyExactTypeAction(state.typeStudio, action.action);
      return {
        ...state,
        typeStudio,
        typeResult: legacyTypeForSession(typeStudio),
        selectedTypeId: typeStudio.current.selectedDirectionId,
      };
    }
    case "setTypeTuning":
      return { ...state, typeTuning: { ...state.typeTuning, ...action.tuning } };
    case "resetTypeTuning":
      return { ...state, typeTuning: defaultTypeTuning() };
    case "typeReady":
      return { ...state, typeResult: action.result, selectedTypeId: action.result.recommendations[0]?.systemId ?? null };
    case "selectType": {
      if (!state.typeStudio) return { ...state, selectedTypeId: action.systemId };
      const typeStudio = applyExactTypeAction(state.typeStudio, { type: "selectDirection", directionId: action.systemId });
      return {
        ...state,
        typeStudio,
        typeResult: legacyTypeForSession(typeStudio),
        selectedTypeId: typeStudio.current.selectedDirectionId,
      };
    }
    case "resetType":
      return resetTypeFields(state);
    case "resetAll":
      return state.entry ? { ...freshState(), entry: "type", projectId: newProjectId() } : freshState();
    case "setConsent":
      return { ...state, consentRemember: action.consent };
    case "projectLoaded":
      return {
        ...state,
        entry: "type",
        brief: { ...action.brief, route: "type" },
        typeStudio: null,
        typeTuning: defaultTypeTuning(),
        typeResult: action.typeResult,
        selectedTypeId: action.selectedTypeId,
        projectId: action.projectId,
        projectName: action.projectName,
        step: "direction",
      };
    default:
      return state;
  }
};

type StudioContextValue = {
  state: StudioState;
  dispatch: React.Dispatch<StudioAction>;
};

const StudioContext = createContext<StudioContextValue | null>(null);

export const StudioProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, undefined, initialState);

  useEffect(() => {
    if (typeof window === "undefined" || state.entry !== "type") return;
    try {
      if (!state.typeStudio || state.typeStudio.recommendation.directions.length === 0) {
        window.sessionStorage.removeItem(ACTIVE_STUDIO_SESSION_KEY);
        return;
      }
      window.sessionStorage.setItem(ACTIVE_STUDIO_SESSION_KEY, JSON.stringify({
        schemaVersion: 1,
        brief: state.brief,
        current: state.typeStudio.current,
        typeTuning: state.typeTuning,
        step: state.step,
        projectId: state.projectId,
        projectName: state.projectName,
      }));
    } catch {
      // Storage may be disabled or full. The live session still works; only
      // refresh recovery becomes unavailable.
    }
  }, [state]);

  const value = useMemo(() => ({ state, dispatch }), [state]);
  return <StudioContext.Provider value={value}>{children}</StudioContext.Provider>;
};

export const useStudio = (): StudioContextValue => {
  const context = useContext(StudioContext);
  if (!context) throw new Error("useStudio must be used inside StudioProvider");
  return context;
};

export const selectDirection = (state: StudioState): DirectionCard | null => {
  if (!state.typeResult) return null;
  return composeDirection({
    projectId: state.projectId,
    ...(state.projectName ? { name: state.projectName } : {}),
    type: state.typeResult,
    ...(state.brief.previewCopy ? { previewCopy: state.brief.previewCopy } : {}),
    selectedTypeSystemId: state.selectedTypeId,
    ...(state.typeStudio ? { typeCatalogOverride: typeCatalogForSession(state.typeStudio) } : {}),
  });
};

export const selectQueue = (state: StudioState) => questionQueue(state.brief);
export const selectPendingCount = (state: StudioState) => pendingQuestionIds(state.brief).length;

/** Test seams: functions stay Fast Refresh-compatible during frontend work. */
export const reduceForTests = reducer;
export const getInitialStateForTests = (): StudioState => initialState();
